/**
 * Native authentication helpers — Sign in with Apple + Google Sign-In.
 *
 * These are Capacitor-only paths. The existing browser-based Google OAuth
 * relay (Emergent) remains untouched for the web build.
 *
 * Contract with the backend:
 *   POST /api/auth/apple        { identityToken, authorizationCode?, nonce?, email?, name? }
 *   POST /api/auth/google/native { idToken, accessToken?, email?, name?, picture? }
 * Both endpoints verify the ID token server-side (Apple JWKS / Google
 * OAuth2) and return the same shape as email/password login:
 *   { user_id, email, name, is_premium, token, picture? }
 *
 * Env vars consumed at build time (never hard-coded; safe to omit on web):
 *   REACT_APP_GOOGLE_IOS_CLIENT_ID      – iOS OAuth client id
 *   REACT_APP_GOOGLE_ANDROID_CLIENT_ID  – Android OAuth client id (optional
 *                                          — Android plugin can use the web
 *                                          client via serverClientId)
 *   REACT_APP_GOOGLE_WEB_CLIENT_ID      – Web OAuth client id (used by the
 *                                          Android side of the plugin as
 *                                          serverClientId, and required to
 *                                          receive an id_token on Android)
 * Native configuration outside JS:
 *   iOS: add the reversed-client-id URL scheme to Info.plist; add the
 *        "Sign in with Apple" capability.
 *   Android: place google-services.json in android/app/ (or configure the
 *        client IDs via GoogleAuth.initialize as done below).
 */

import { Capacitor } from '@capacitor/core';
import { isNativePlatform } from './native';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Literal specifiers so webpack can bundle each plugin (a variable import()
// cannot be resolved and fails at runtime on iOS).
const _pluginLoaders = {
  '@capacitor-community/apple-sign-in': () => import('@capacitor-community/apple-sign-in'),
  '@codetrix-studio/capacitor-google-auth': () => import('@codetrix-studio/capacitor-google-auth'),
};

const _lazyImport = async (mod) => {
  try {
    return await _pluginLoaders[mod]();
  } catch (e) {
    console.warn('[native-auth] plugin unavailable:', mod, e?.message || e);
    return null;
  }
};

export const GOOGLE_NATIVE_FLAG_KEY = 'futureself_google_native';

/** Cryptographically random nonce, hex-encoded. Also returns its SHA-256
 *  hex digest — the plugin/Apple pipeline expects the raw string, Apple
 *  echoes back a SHA-256 hex digest in the identity token's `nonce` claim.
 *  Both are sent to the backend so it can accept either variant. */
const _generateNonce = async () => {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  const raw = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return raw;
};

// --------------------------------------------------------------------
// Apple Sign-In
// --------------------------------------------------------------------

export const isAppleSignInSupported = () =>
  isNativePlatform() && Capacitor.getPlatform() === 'ios';

/** Error carrying the backend `code` (e.g. no_account) so callers can branch. */
export class ProviderAuthError extends Error {
  constructor(message, code, status) {
    super(message);
    this.code = code;
    this.status = status;
  }
}

/** POST a verified-on-server provider credential. allowCreate=false asks before creating. */
export const postProviderSignIn = async (provider, cred, allowCreate) => {
  const path = provider === 'apple' ? 'apple' : 'google/native';
  const res = await fetch(`${API_URL}/api/auth/${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ ...cred, allow_create: allowCreate }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const fallback = provider === 'apple' ? 'Apple sign-in failed.' : 'Google sign-in failed.';
    throw new ProviderAuthError(typeof data?.detail === 'string' ? data.detail : fallback, data?.code, res.status);
  }
  if (provider === 'google' && isNativePlatform()) {
    // Remember the provider so sign-out only calls Google for Google users.
    try { localStorage.setItem(GOOGLE_NATIVE_FLAG_KEY, '1'); } catch { /* ignore */ }
  }
  return data;
};

/** Native Apple sheet → credential (not yet sent). Pass a server nonce when linking. */
export const getAppleNativeCredential = async (serverNonce) => {
  if (!isAppleSignInSupported()) {
    throw new Error('Sign in with Apple is only available on iOS.');
  }
  const mod = await _lazyImport('@capacitor-community/apple-sign-in');
  const SignInWithApple = mod?.SignInWithApple;
  if (!SignInWithApple) throw new Error('Apple Sign-In plugin missing.');

  const nonce = serverNonce || await _generateNonce();
  const state = await _generateNonce();

  const options = {
    // clientId must match the iOS Bundle ID with SIWA enabled.
    clientId: process.env.REACT_APP_APPLE_CLIENT_ID || 'es.futureself.app',
    // The redirectURI is unused on native iOS (Apple returns the token
    // directly to the app) but the plugin requires the field.
    redirectURI: process.env.REACT_APP_APPLE_REDIRECT_URI || 'https://app.futureself.es/auth/apple/callback',
    scopes: 'email name',
    state,
    nonce,
  };
  const result = await SignInWithApple.authorize(options);
  const r = result?.response || {};
  return {
    identityToken: r.identityToken,
    authorizationCode: r.authorizationCode,
    nonce, // raw nonce; backend hashes for comparison against claim
    email: r.email, // present on the FIRST sign-in only
    name: [r.givenName, r.familyName].filter(Boolean).join(' ').trim() || undefined,
  };
};

/** Trigger Apple Sign-In and exchange the identity token with the backend. */
export const signInWithApple = async (allowCreate = true) =>
  postProviderSignIn('apple', await getAppleNativeCredential(), allowCreate);

// --------------------------------------------------------------------
// Google Sign-In (native)
// --------------------------------------------------------------------

let _googleInitialised = false;

const _initGoogleOnce = async (GoogleAuth) => {
  if (_googleInitialised) return;
  try {
    await GoogleAuth.initialize({
      // iOS uses `iosClientId` (the OAuth iOS client). Android uses
      // `serverClientId` (the WEB client) to receive an id_token.
      iosClientId: process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID || undefined,
      androidClientId: process.env.REACT_APP_GOOGLE_ANDROID_CLIENT_ID || undefined,
      // iOS plugin uses `clientId` before `iosClientId`; the web ID there crashes GoogleSignIn (URL scheme mismatch).
      clientId: (Capacitor.getPlatform() === 'ios'
        ? process.env.REACT_APP_GOOGLE_IOS_CLIENT_ID
        : process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID) || '',
      serverClientId: process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID || undefined,
      scopes: ['profile', 'email'],
      grantOfflineAccess: false,
    });
    _googleInitialised = true;
  } catch (e) {
    // Older devices / re-init attempts throw — safe to ignore, plugin
    // still functions if it was previously initialised.
    console.warn('[native-auth] GoogleAuth.initialize:', e?.message || e);
  }
};

export const isNativeGoogleSignInSupported = () => isNativePlatform();

/** Native Google sheet → credential (not yet sent). */
export const getGoogleNativeCredential = async () => {
  if (!isNativeGoogleSignInSupported()) {
    throw new Error('Native Google Sign-In not available on the web build.');
  }
  const mod = await _lazyImport('@codetrix-studio/capacitor-google-auth');
  const GoogleAuth = mod?.GoogleAuth;
  if (!GoogleAuth) throw new Error('Google Auth plugin missing.');

  await _initGoogleOnce(GoogleAuth);

  const gu = await GoogleAuth.signIn();
  const idToken = gu?.authentication?.idToken;
  if (!idToken) throw new Error('Google sign-in returned no idToken.');
  return {
    idToken,
    accessToken: gu?.authentication?.accessToken,
    email: gu?.email,
    name: gu?.name,
    picture: gu?.imageUrl,
  };
};

/** Trigger native Google Sign-In and exchange the id token with the backend. */
export const signInWithGoogleNative = async (allowCreate = true) =>
  postProviderSignIn('google', await getGoogleNativeCredential(), allowCreate);

/** Native sign-out — clear plugin state so the account picker shows next
 *  time. Safe no-op on the web (returns immediately). */
export const nativeSignOut = async () => {
  if (!isNativePlatform()) return;
  try {
    const mod = await _lazyImport('@codetrix-studio/capacitor-google-auth');
    if (mod?.GoogleAuth?.signOut) {
      // The iOS plugin crashes on signOut if it was never initialised.
      await _initGoogleOnce(mod.GoogleAuth);
      await mod.GoogleAuth.signOut();
    }
  } catch (e) {
    console.info('[native-auth] Google signOut:', e?.message || e);
  }
};
